import { Component, OnDestroy, OnInit } from "@angular/core";
import { Params, Router, ActivatedRoute } from "@angular/router";
import { CHttp } from "../../core/services/chttp.service";
import { config } from "../../app.config";
import { AuthDataStorageService } from "../../auth/services/auth-data-storage.service";
import { MemberService } from "../services/member.service";
import { LocationService } from "../../locations/services/location.service";
import { BaseEditComponent } from "../../core/common/base-component";
import swal from "sweetalert2";
import { FormUtil } from "../../core/form.util";
import { JobCategoriesService } from "../../job-categories/services/job-categories.service";
import { MemberEvent } from "../members.interfaces";
import * as moment from "moment";
import { ReferralsService } from "../../referrals/referrals.service";
import {
  Referral,
  MemberReferrals,
  RewardInstance,
  RewardProgress,
} from "../../referrals/referrals.interfaces";
import { environment } from "../../../environments/environment";
import { Title } from "@angular/platform-browser";
const alertify = require("alertify.js");

@Component({
  selector: "ch-member-edit",
  templateUrl: "./member-edit.component.html",
  styleUrls: ["./member-edit.component.css"],
})
export class MemberEditComponent extends BaseEditComponent implements OnInit, OnDestroy {
  public memberId: string;
  public memberData: any = {};
  public name: string;
  public cities: any[];
  public jobCategories: any[];
  public events: MemberEvent[];
  public userName: string;
  public approving: boolean;
  public showWebProfileModal: boolean;
  public showAbTestsModal: boolean;
  public showSubscriptionModal: boolean;
  public showCancelMemberModal = false;
  public showNewIdModal = false;
  public showInvalidPlanDialog = false;
  public showMemberDeleteModal = false;
  public currentNotes: string;
  private initialData: any;
  public referrals: MemberReferrals = {
    pendingReferrals: 0,
    successfulReferrals: [],
    rewardProgress: [],
  };
  public rewardInstances: RewardInstance[] = [];

  constructor(
    private http: CHttp,
    private authDataStorageService: AuthDataStorageService,
    private memberService: MemberService,
    private locationService: LocationService,
    private jobCategoriesService: JobCategoriesService,
    private referralsService: ReferralsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private titleService: Title,
  ) {
    super();
  }

  ngOnInit() {
    alertify.logPosition("bottom right");

    this.loadData();

    this.getCities();
    this.getJobCategories();
    this.getReferrals();
    this.getRewardInstances();
  }

  ngOnDestroy() {
    this.titleService.setTitle(`CODE Hospitality CMS`);
  }

  loadData() {
    this.activatedRoute.queryParamMap
      .map((params: Params) => params.params)
      .subscribe((params) => {
        this.memberId = params.id;

        this.loading = true;
        this.memberService.getMember(this.memberId).subscribe(
          (data) => {
            this.loading = false;
            this.loaded = true;
            this.memberData = data.json();
            this.titleService.setTitle(`${this.memberData.firstName} ${this.memberData.lastName} | Members | CODE Hospitality CMS`);
            this.initialData = { ...this.memberData };

            this.name =
              this.memberData.firstName + " " + this.memberData.lastName;
            if (
              "PROMO_CODE" === this.memberData.acquisitionChannel ||
              "DISCOUNT" === this.memberData.acquisitionChannel
            ) {
              this.memberData.vipYes = true;
            } else if (
              "STAFF_INCENTIVE" === this.memberData.acquisitionChannel
            ) {
              this.memberData.vipNo = true;
            }

            if (this.memberData.proofResourceId) {
              this.memberData.proofResourceUrl =
                config.api_url + "resources/" + this.memberData.proofResourceId;

              this.http
                .get(this.memberData.proofResourceUrl)
                .toPromise()
                .then((response) => {
                  this.memberData.proofResourceIsImage = response.headers
                    .get("content-type")
                    .startsWith("image");
                });
            }
            this.getEventLogEntries();
          },
          (error) => {
            this.loading = false;
            this.alertError(error);
          },
        );
      });
  }

  private getReferrals() {
    this.loading = true;
    this.referralsService.getReferralsForMemberSuccess(this.memberId).subscribe(
      (resp) => {
        this.referrals.successfulReferrals = resp.json();
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
    this.referralsService.getReferralsForMemberProgress(this.memberId).subscribe(
      (resp) => {
        const data = resp.json();
        this.referrals.pendingReferrals = data.pendingReferralsCount;
        this.referrals.rewardProgress = data.rewardProgress;
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getRewardInstances() {
    this.loading = true;
    this.referralsService
      .getReferralRewardInstancesForMember(this.memberId)
      .subscribe(
      (resp) => {
        this.rewardInstances = resp.json();
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getCities() {
    this.locationService.getLocationCities().subscribe(
      (resp) => {
        this.cities = resp.json();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getJobCategories() {
    this.jobCategoriesService.getAllJobCategories().subscribe(
      (resp) => {
        this.jobCategories = resp.json();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private getEventLogEntries() {
    this.memberService.getMemberEvents(this.memberId).subscribe(
      (resp) => {
        this.events = resp
          .json()
          .map((event): MemberEvent => {
            return {
              ...event,
              createdAt: moment(event.createdAt),
              parsedDetails: this.parseDatetime(event.details),
            };
          })
          .sort(
          (a: MemberEvent, b: MemberEvent) => -a.createdAt.diff(b.createdAt),
        );
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public edit(form) {
    if (!FormUtil.isValid(form)) {
      return;
    }

    this.memberData.notes = this.currentNotes;
    const reqData = this.buildReqData(this.memberData);

    this.loading = true;
    this.memberService.updateMember(reqData, this.memberId).subscribe(
      (resp) => {
        this.loading = false;
        this.cancel();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private buildReqData(data: any) {
    const reqData = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      employer: data.employer,
      jobCategoryId: data.jobCategoryId,
      jobTitle: data.jobTitle,
      locationId: data.locationId,
      notes: data.notes,
      acquisitionChannel: data.acquisitionChannel,
      country: data.country,
      city: data.city,
      zipCode: data.zipCode,
      referralsEnabledFlag: !!data.referralsEnabledFlag,
    };
    return reqData;
  }

  public cancel() {
    this.router.navigateByUrl("/admin/members");
  }

  public requestNewId(type: string) {
    this.pageLoading = true;
    this.memberService.requestNewId(this.memberId, type).subscribe(
      (resp) => {
        swal("Congratulations", "New id request successfully sent.", "success");
        this.renewStatus();
        this.pageLoading = false;
        this.loadData();
      },
      (error) => {
        this.pageLoading = false;
        this.alertError(error);
      },
    );
  }

  public approve() {
    if (this.approving) {
      return;
    }
    this.approving = true;
    this.loading = true;
    this.memberService.acceptMember(this.memberId).subscribe(
      (resp) => {
        this.renewStatus();
        this.memberData.lastStatusUpdate = new Date();
        this.approving = false;
        this.loadData();
      },
      (error) => {
        this.approving = false;
        this.loading = false;
        if (
          error.status == 400 &&
          error.error.startsWith("Couldn't find product plan")
        ) {
          this.showInvalidPlanDialog = true;
        } else {
          this.alertError(error);
        }
      },
    );
  }

  public downloadProof() {
    const a = document.createElement("a");
    a.href =
      this.memberData.proofResourceUrl +
      `?x-auth-token=${this.authDataStorageService.getSessionTokenFromStorage()}`;
    a.download = this.memberData.proofResourceUrl.split("/").pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  public reject() {
    this.memberService.rejectMember(this.memberId).subscribe(
      (resp) => {
        this.renewStatus();
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public resetPassword(isDirtyEmail) {
    if (isDirtyEmail) {
      return;
    }

    this.pageLoading = true;
    this.memberService.forgottenPassword(this.memberData.email).subscribe(
      (resp) => {
        swal(
          "Congratulations",
          "Reset password request is successfully sent.",
          "success",
        );
        this.pageLoading = false;
        this.loadData();
      },
      (error) => {
        this.pageLoading = false;
      },
    );
  }

  public suspend() {
    const methodName = this.isSuspended ? "unsuspendMember" : "suspendMember";
    this.pageLoading = true;
    this.memberService[methodName](this.memberId).subscribe(
      (resp) => {
        this.renewStatus();
        this.pageLoading = false;
        swal(
          "Congratulations",
          "Suspend status successfully changed.",
          "success",
        );
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public toggleWebProfileDialog(): void {
    this.showWebProfileModal = true;
  }

  public closeWebProfileDialog(): void {
    this.showWebProfileModal = false;
  }

  public toggleMemberDeleteDialog(): void {
    this.showMemberDeleteModal = true;
  }

  public closeMemberDeleteDialog(): void {
    this.showMemberDeleteModal = false;
  }

  public toggleAbTestsDialog(): void {
    this.showAbTestsModal = true;
  }

  public closeAbTestsDialog(): void {
    this.showAbTestsModal = false;
  }

  public toggleCancelMemberDialog(): void {
    this.showCancelMemberModal = true;
  }

  public closeCancelMemberDialog(): void {
    this.showCancelMemberModal = false;
  }

  public closeInvalidPlanDialog(): void {
    this.showInvalidPlanDialog = false;
  }

  public cancelSubscriptionEndOfPeriod(data): void {
    this.loading = true;
    this.memberService.cancelSubscriptionEndOfPeriod(this.memberId).subscribe(
      (resp) => {
        alertify.success(
          `Subscription marked for cancelling at end of the subscription period.`,
        );
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public cancelSubscriptionNow(data): void {
    this.loading = true;
    this.memberService.cancelSubscriptionNow(this.memberId).subscribe(
      (resp) => {
        alertify.success(`Subscription is canceled successfully.`);
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public makeVip() {
    this.initialData.isVip = true;
    this.pageLoading = true;
    this.memberService.updateVipStatus(this.memberId, true).subscribe(
      (resp) => {
        this.renewStatus();
        this.memberData.isVip = true;
        this.pageLoading = false;
        swal(
          "Congratulations",
          "Successfully changed member vip status",
          "success",
        );
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public revokeVip() {
    this.initialData.isVip = false;
    this.pageLoading = true;
    this.memberService.updateVipStatus(this.memberId, false).subscribe(
      (resp) => {
        this.renewStatus();
        this.memberData.isVip = false;
        this.pageLoading = false;
        swal(
          "Congratulations",
          "Successfully changed member vip status",
          "success",
        );
        this.loadData();
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  public addSubscription() {
    this.showSubscriptionModal = true;
  }

  public onSubscriptionAdded(data: any) {
    this.memberService.addSubscription(this.memberData.id, data).subscribe(
      () => {
        swal(
          "Congratulations",
          "The subscription was added successfully.",
          "success",
        );
        this.loadData();
      },
      (error) => {
        let message = `There was a problem adding the subscription.`;
        if (409 === error.status && "DUPLICATE_DATA" === error.error) {
          message = "A subscription with this purchase Id already exists";
        }

        alertify.error(message);
      },
    );
  }

  public onSubscriptionDialogClosed() {
    this.showSubscriptionModal = false;
  }

  get showButtons() {
    return (
      "WAITING_FOR_APPROVAL" === this.memberData.status ||
      "WAITING_FOR_PROOF" === this.memberData.status
    );
  }

  get isAccepted() {
    return "APPROVED" === this.memberData.status;
  }

  get isRejected() {
    return "REJECTED" === this.memberData.status;
  }

  get isSuspended() {
    return "SUSPENDED" === this.memberData.status;
  }

  get isValidAc() {
    return Boolean(null != this.memberData.acquisitionChannel);
  }

  private renewStatus() {
    this.memberService.getMember(this.memberData.id).subscribe(
      (resp) => {
        this.memberData.status = resp.json().status;
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.alertError(error);
      },
    );
  }

  private alertSuccess(method): void {
    alertify.success(`Successfully ${method} member.`);
  }

  private alertError(error: any) {
    alertify.error(
      `There was a ${error.status} error with message '${error.error}'.`,
    );
  }

  public parseDatetime(str: String) {
    if (str == null || str.trim() == "") return str;

    const datetime = /\d{4}(.\d{2}){2}(\s|T)(\d{2}.){2}\d{2}/g;
    return str.replace(datetime, function replacer(match) {
      return moment(match).format("HH:mm DD MMM YYYY");
    });
  }

  get stripeBaseUrl() {
    return `https://dashboard.stripe.com/${!environment.production ? 'test/' : ''}`;
  }

  get stripeCustomerLink() {
    return `${this.stripeBaseUrl}customers/${this.memberData.customerId}`;
  }

  get referrerProfileLink() {
    return `admin/members/edit?id=${this.memberData.referrerId}`;
  }

  public refereeProfileLink(referral: Referral) {
    return `admin/members/edit?id=${referral.refereeId}`;
  }

  public refereeStripeSubscriptionLink(referral: Referral) {
    return `${this.stripeBaseUrl}subscriptions/${referral.stripeSubscriptionId}`;
  }

  public programLink(rewardProgress: RewardProgress) {
    return `admin/referral-programs/edit?id=${rewardProgress.programId}`;
  }

  public rewardLink(rewardProgress: RewardProgress) {
    return `admin/referral-rewards/edit?referralProgramId=${rewardProgress.programId}&id=${rewardProgress.rewardId}`;
  }

  public rewardInstanceLink(rewardInstance: RewardInstance) {
    return `admin/referral-rewards/edit?referralProgramId=${rewardInstance.referralProgramId}&id=${rewardInstance.referralRewardId}`;
  }

  public markReferralsEnabledFlag(checked: boolean) {
    this.memberData.referralsEnabledFlag = checked;
  }
}



// WEBPACK FOOTER //
// /Users/thxgg/Projects/Work/code-hospitality-cms/src/app/member/member-edit/member-edit.component.ts